import React, { useEffect, useRef, useState } from "react";
import { api, getErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash, Plus, ArrowSquareOut, CheckCircle, ArrowUp, ArrowDown, UploadSimple } from "@phosphor-icons/react";

const Card = ({ title, children }) => (
  <div className="border border-[#E5E7EB] bg-white p-5">
    <div className="font-display text-base font-black tracking-tight">{title}</div>
    <div className="mt-4">{children}</div>
  </div>
);

const Label = ({ children }) => (
  <div className="mb-1 mt-3 text-xs font-bold text-[#030712]">{children}</div>
);

const Num = ({ value, onChange, testid, step = "1" }) => (
  <Input type="number" step={step} min="0" value={value ?? ""} data-testid={testid}
    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />
);

export default function AdminCleaningFunnel() {
  const [data, setData] = useState(null);
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data: ov } = await api.get("/cleaning/admin/overview");
      setData(ov);
      setC(ov.config);
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  const loadGallery = async () => {
    try {
      const { data: g } = await api.get("/cleaning/admin/gallery");
      setGallery(g.photos || []);
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  useEffect(() => { load(); loadGallery(); }, []);

  const uploadPhotos = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("alt", f.name.replace(/\.[^.]+$/, "").slice(0, 200));
      try {
        await api.post("/cleaning/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (e) {
        fail += 1;
        toast.error(`${f.name}: ${getErr(e)}`);
      }
    }
    if (ok) toast.success(`Uploaded ${ok} photo${ok > 1 ? "s" : ""} — auto-resized and live on /cleaning.`);
    if (fileRef.current) fileRef.current.value = "";
    await loadGallery();
    setUploading(false);
  };

  const deletePhoto = async (photo_id) => {
    if (!window.confirm("Remove this photo from the cleaning page?")) return;
    try {
      await api.delete(`/cleaning/admin/gallery/${photo_id}`);
      toast.success("Photo removed.");
      loadGallery();
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  const updateAlt = async (photo_id, alt) => {
    try {
      await api.put(`/cleaning/admin/gallery/${photo_id}`, { alt });
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  const movePhoto = async (idx, dir) => {
    const next = [...gallery];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setGallery(next);
    try {
      await api.post("/cleaning/admin/gallery/reorder", { order: next.map((p) => p.photo_id) });
    } catch (e) {
      toast.error(getErr(e));
      loadGallery();
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put("/cleaning/admin/config", c);
      setC(res.config);
      toast.success("Pricing saved — live on /cleaning immediately.");
    } catch (e) {
      toast.error(getErr(e));
    }
    setSaving(false);
  };

  const complete = async (id) => {
    try {
      await api.post(`/cleaning/admin/quotes/${id}/complete`);
      toast.success("Marked completed — post-service email sequence scheduled.");
      load();
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  if (!data || !c) return <div className="px-5 py-10 text-sm text-[#4B5563]">Loading…</div>;
  const an = data.analytics || {};
  const quotes = data.quotes || [];
  const setNum = (k) => (v) => setC({ ...c, [k]: v });

  return (
    <div className="px-5 py-8 md:px-10 md:py-10" data-testid="admin-cleaning-page">
      <div className="font-mono-label">Cleaning Sales Funnel</div>
      <h1 className="font-display text-3xl font-black tracking-tight md:text-4xl">Cleaning Funnel</h1>
      <p className="mt-2 max-w-2xl text-sm text-[#4B5563]">
        Pricing changes here go live on{" "}
        <a className="font-bold text-[#0044FF]" href="/cleaning" target="_blank" rel="noreferrer">
          hcobnetwork.com/cleaning <ArrowSquareOut className="inline" size={12} />
        </a>{" "}
        instantly — no deploy. Bookings land in the Quotes inbox tagged CLEANING FUNNEL. Client contact is EMAIL ONLY (A2P compliance).
      </p>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        {[
          ["Page views", an.page_view || 0],
          ["Quote starts", an.quote_start || 0],
          ["Quotes completed", an.quote_complete || 0],
          ["Bookings", an.booking || 0],
          ["Cross-sell clicks", an.cross_sell_click || 0],
        ].map(([label, n]) => (
          <div key={label} className="border border-[#E5E7EB] bg-white p-4" data-testid={`cl-stat-${label}`}>
            <div className="font-display text-2xl font-black">{n}</div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">{label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card title="Base pricing (Routine algorithm)">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Base price (1 BR)</Label><Num value={c.base_price} onChange={setNum("base_price")} testid="cl-cfg-base" /></div>
            <div><Label>Per extra bedroom</Label><Num value={c.per_bedroom} onChange={setNum("per_bedroom")} /></div>
            <div><Label>Per extra full bath</Label><Num value={c.per_full_bath} onChange={setNum("per_full_bath")} /></div>
            <div><Label>Per half bath</Label><Num value={c.per_half_bath} onChange={setNum("per_half_bath")} /></div>
            <div><Label>Deep multiplier</Label><Num step="0.05" value={c.deep_multiplier} onChange={setNum("deep_multiplier")} /></div>
            <div><Label>Move-out multiplier</Label><Num step="0.05" value={c.moveout_multiplier} onChange={setNum("moveout_multiplier")} /></div>
            <div><Label>Targeted per-bath rate</Label><Num value={c.targeted_per_bath} onChange={setNum("targeted_per_bath")} /></div>
            <div><Label>Minimum job value</Label><Num value={c.min_job} onChange={setNum("min_job")} /></div>
            <div><Label>Deep clean minimum</Label><Num value={c.deep_min_job} onChange={setNum("deep_min_job")} testid="cl-cfg-deep-min" /></div>
          </div>
        </Card>

        <Card title="Add-ons, discounts & payment">
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(c.addons || {}).map(([k, v]) => (
              <div key={k}>
                <Label>Add-on: {k}</Label>
                <Num value={v} onChange={(nv) => setC({ ...c, addons: { ...c.addons, [k]: nv } })} />
              </div>
            ))}
            {["weekly", "biweekly", "monthly"].map((k) => (
              <div key={k}>
                <Label>{k} discount %</Label>
                <Num value={c.recurring_discounts?.[k]} onChange={(nv) => setC({ ...c, recurring_discounts: { ...c.recurring_discounts, [k]: nv } })} />
              </div>
            ))}
            <div><Label>Deposit % at booking</Label><Num value={c.deposit_pct} onChange={setNum("deposit_pct")} testid="cl-cfg-deposit" /></div>
            <div><Label>Card fee %</Label><Num step="0.1" value={c.card_fee_pct} onChange={setNum("card_fee_pct")} /></div>
            <div><Label>Card fee flat $</Label><Num step="0.05" value={c.card_fee_flat} onChange={setNum("card_fee_flat")} /></div>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm font-bold">
            <input type="checkbox" checked={!!c.show_insured} data-testid="cl-cfg-insured"
              onChange={(e) => setC({ ...c, show_insured: e.target.checked })} />
            Show "insured" on the page (only after coverage is confirmed in writing)
          </label>
        </Card>

        <Card title={`Client reviews on the page (${(c.reviews || []).length})`}>
          {(c.reviews || []).map((r, i) => (
            <div key={i} className="mb-3 border border-[#E5E7EB] p-2">
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="First name" value={r.name} onChange={(e) => { const l = [...c.reviews]; l[i] = { ...l[i], name: e.target.value }; setC({ ...c, reviews: l }); }} />
                <Input placeholder="Neighborhood" value={r.neighborhood} onChange={(e) => { const l = [...c.reviews]; l[i] = { ...l[i], neighborhood: e.target.value }; setC({ ...c, reviews: l }); }} />
                <Input placeholder="Service type" value={r.service} onChange={(e) => { const l = [...c.reviews]; l[i] = { ...l[i], service: e.target.value }; setC({ ...c, reviews: l }); }} />
              </div>
              <div className="mt-1 flex gap-2">
                <textarea className="w-full border border-[#E5E7EB] p-2 text-sm" rows={2} placeholder="Review text"
                  value={r.text} onChange={(e) => { const l = [...c.reviews]; l[i] = { ...l[i], text: e.target.value }; setC({ ...c, reviews: l }); }} />
                <button className="text-[#EF4444]" onClick={() => setC({ ...c, reviews: c.reviews.filter((_, j) => j !== i) })}><Trash size={16} /></button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" data-testid="cl-add-review"
            onClick={() => setC({ ...c, reviews: [...(c.reviews || []), { name: "", neighborhood: "", service: "", text: "" }] })}>
            <Plus size={14} /> Add review
          </Button>
          <div className="mt-2 text-[11px] text-[#4B5563]">The social-proof section stays hidden on the page until at least one review is added.</div>
        </Card>

        <Card title={`Photo gallery (${gallery.length})`}>
          <p className="text-sm text-[#4B5563]">
            Upload any photo — phone, camera, screenshot. Every upload is auto-resized and cropped to a uniform 4:3 tile so it fits the cleaning page perfectly. Drag to reorder using the arrows. Changes are live instantly.
          </p>
          <div className="mt-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => uploadPhotos(e.target.files)}
              data-testid="cl-gallery-file-input"
            />
            <Button
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={uploading}
              data-testid="cl-gallery-upload-btn"
            >
              <UploadSimple size={14} /> {uploading ? "Uploading…" : "Upload photos"}
            </Button>
            <span className="ml-2 text-[11px] text-[#4B5563]">JPG / PNG / HEIC · up to 15 MB each</span>
          </div>
          {!gallery.length && (
            <div className="mt-4 border border-dashed border-[#E5E7EB] p-4 text-xs text-[#4B5563]" data-testid="cl-gallery-empty">
              No custom photos yet — the cleaning page is showing the default sample gallery. Upload your own to replace them.
            </div>
          )}
          {!!gallery.length && (
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
              {gallery.map((p, i) => (
                <div key={p.photo_id} className="border border-[#E5E7EB] bg-white p-2" data-testid={`cl-gallery-tile-${p.photo_id}`}>
                  <div className="relative">
                    <img
                      src={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/cleaning/gallery/${p.photo_id}.jpg`}
                      alt={p.alt}
                      className="aspect-[4/3] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <Input
                    className="mt-2 text-xs"
                    defaultValue={p.alt}
                    placeholder="Alt text (for SEO / accessibility)"
                    onBlur={(e) => e.target.value !== p.alt && updateAlt(p.photo_id, e.target.value)}
                    data-testid={`cl-gallery-alt-${p.photo_id}`}
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex gap-1">
                      <button
                        className="border border-[#E5E7EB] p-1 disabled:opacity-40"
                        onClick={() => movePhoto(i, -1)}
                        disabled={i === 0}
                        title="Move up"
                        data-testid={`cl-gallery-up-${p.photo_id}`}
                      >
                        <ArrowUp size={13} />
                      </button>
                      <button
                        className="border border-[#E5E7EB] p-1 disabled:opacity-40"
                        onClick={() => movePhoto(i, 1)}
                        disabled={i === gallery.length - 1}
                        title="Move down"
                        data-testid={`cl-gallery-down-${p.photo_id}`}
                      >
                        <ArrowDown size={13} />
                      </button>
                    </div>
                    <button
                      className="text-[#EF4444]"
                      onClick={() => deletePhoto(p.photo_id)}
                      title="Remove"
                      data-testid={`cl-gallery-delete-${p.photo_id}`}
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Save">
          <p className="text-sm text-[#4B5563]">All values above apply instantly to the live calculator and follow-up emails once saved.</p>
          <Button className="mt-4" disabled={saving} onClick={save} data-testid="cl-save-config">
            {saving ? "Saving…" : "Save pricing & settings"}
          </Button>
        </Card>
      </div>

      <div className="mt-6">
        <div className="font-display text-lg font-black">Quotes & bookings ({quotes.length} recent · {data.bookings_count} booked)</div>
        <div className="mt-3 overflow-x-auto border border-[#E5E7EB] bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-[11px] uppercase tracking-wider text-[#4B5563]">
                <th className="p-3">When</th><th className="p-3">Service</th><th className="p-3">Home</th>
                <th className="p-3">Price</th><th className="p-3">Email</th><th className="p-3">Status</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.quote_id} className="border-b border-[#F3F4F6]" data-testid={`cl-quote-row-${q.quote_id}`}>
                  <td className="p-3 text-xs text-[#4B5563]">{q.created_at ? new Date(q.created_at).toLocaleString() : "—"}</td>
                  <td className="p-3 font-bold">{q.service_label}</td>
                  <td className="p-3 text-xs">{q.service_type === "targeted" ? `${(q.addons || []).length} items` : `${q.bedrooms}BR`} · {q.full_baths}F/{q.half_baths}H · {q.zip}</td>
                  <td className="p-3 font-bold">${Number(q.price).toFixed(0)}</td>
                  <td className="p-3 text-xs">{q.booking?.name ? `${q.booking.name} · ` : ""}{q.email || "—"}</td>
                  <td className="p-3">
                    {q.completed_at ? (
                      <span className="bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-bold text-[#15803D]">COMPLETED</span>
                    ) : q.booked ? (
                      <span className="bg-[#EFF6FF] px-2 py-0.5 text-[10px] font-bold text-[#1D4ED8]">BOOKED{q.booking?.date ? ` · ${q.booking.date}` : ""}</span>
                    ) : q.email ? (
                      <span className="bg-[#FFFBEB] px-2 py-0.5 text-[10px] font-bold text-[#D97706]">SAVED · recovery emails on</span>
                    ) : (
                      <span className="bg-[#F3F4F6] px-2 py-0.5 text-[10px] font-bold text-[#4B5563]">QUOTED</span>
                    )}
                  </td>
                  <td className="p-3">
                    {q.booked && !q.completed_at && (
                      <Button size="sm" variant="outline" onClick={() => complete(q.quote_id)} data-testid={`cl-complete-${q.quote_id}`}>
                        <CheckCircle size={13} /> Mark completed
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!quotes.length && <tr><td colSpan={7} className="p-6 text-center text-xs text-[#4B5563]">No quotes yet — the funnel is live at /cleaning.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[11px] text-[#4B5563]">
          "Mark completed" starts the post-service email sequence: day-after review request → +7d network intro → +30d rebook (one-time clients).
        </div>
      </div>
    </div>
  );
}
